import {
  isMetaPixelAvailable,
  isMetaPixelConsentGiven,
} from "@/utils/metaPixelConsent";

type MetaPixelContent = {
  id: string;
  quantity: number;
  item_price: number;
};

type MetaPixelEventData = Record<string, unknown>;

function sendMetaPixelEvent(
  type: "track",
  eventName: string,
  data?: MetaPixelEventData,
): void;
function sendMetaPixelEvent(
  type: "trackCustom",
  eventName: string,
  data?: MetaPixelEventData,
): void;
function sendMetaPixelEvent(
  type: "track" | "trackCustom",
  eventName: string,
  data?: MetaPixelEventData,
) {
  if (
    typeof window === "undefined" ||
    !isMetaPixelAvailable() ||
    !isMetaPixelConsentGiven() ||
    !window.fbq
  ) {
    return;
  }

  if (type === "track") {
    window.fbq("track", eventName, data);
  } else {
    window.fbq("trackCustom", eventName, data);
  }
}

export const convertToMetaPixelContents = (items: MetaPixelEventData[]) =>
  items.map((item) => ({
    id: String(item.item_id ?? ""),
    quantity: Number(item.quantity) || 1,
    item_price: Number(item.price) || 0,
  } satisfies MetaPixelContent));

export const sendMetaCheckoutEvent = (
  value: number,
  currency: string = "EUR",
  items: MetaPixelEventData[],
  coupon?: string,
) => {
  const contents = convertToMetaPixelContents(items);
  const numItems = contents.reduce((sum, item) => sum + item.quantity, 0);
  sendMetaPixelEvent("track", "InitiateCheckout", {
    value,
    currency,
    contents,
    num_items: numItems,
  });
  sendMetaPixelEvent("trackCustom", "checkout_event", {
    value,
    currency,
    num_items: numItems,
    coupon: coupon || undefined,
  });
};

export const sendMetaPurchaseEvent = (
  transactionId: string,
  value: number,
  currency: string = "EUR",
  items: MetaPixelEventData[],
) => {
  const contents = convertToMetaPixelContents(items);
  sendMetaPixelEvent("track", "Purchase", {
    value,
    currency,
    transaction_id: transactionId,
    contents,
    num_items: contents.reduce((sum, item) => sum + item.quantity, 0),
  });
};

export const sendMetaFormSubmission = () => {
  sendMetaPixelEvent("track", "Lead");
  sendMetaPixelEvent("trackCustom", "form_submission");
};

export const sendMetaFormInteraction = () =>
  sendMetaPixelEvent("trackCustom", "form_interaction");

export const sendMetaOrderSubmitted = () =>
  sendMetaPixelEvent("trackCustom", "Order");

export const sendMetaContactFormEvent = (source = "main") => {
  const data = {source: `contact_form_${source}_page`};
  sendMetaPixelEvent("track", "Contact", data);
  sendMetaPixelEvent("trackCustom", "contact_form", data);
};

export const sendMetaReviewSelection = (label: string, action: string) =>
  sendMetaPixelEvent("trackCustom", action, {label});

export const sendMetaFunnelOption = (funnelVariant: string | null) =>
  sendMetaPixelEvent("trackCustom", "checkout_funnel", {
    variant: funnelVariant,
  });

export const sendMetaAddToCartEvent = (
  value: number,
  currency: string = "EUR",
  items: MetaPixelEventData[],
) => {
  sendMetaPixelEvent("track", "AddToCart", {
    value,
    currency,
    contents: convertToMetaPixelContents(items),
  });
};

export const sendMetaViewCartEvent = (
  value: number,
  currency: string = "EUR",
  items: MetaPixelEventData[],
) => {
  const contents = convertToMetaPixelContents(items);
  sendMetaPixelEvent("trackCustom", "view_cart", {
    value,
    currency,
    contents,
    num_items: contents.reduce((sum, item) => sum + item.quantity, 0),
  });
};
